import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import React, { useRef, useState } from 'react';
import { Modal, Form, Button } from 'tea-component';
import { useIntl, useHistory } from 'umi';
import {
  ModelType,
  PromptConfirmItemType,
  PromptConfirmType,
  PromptListType,
  ScenarioType,
} from '../../types';
import { useConfirm } from '../../hooks/useConfirm';
import { getDefaultPromptText, getDefaultPromptMap } from '../../utils';
import HistoricalPrompt from '../../components/HistoricalPrompt';
import { DBO_TM } from '@op/utils/format';
import { useSaveDetailForm } from '../../hooks/useSaveForm';
import './index.scss';
import { getReportSuffix } from '@op/utils/time';
import SummaryModeForm from './SummaryModeForm';
import SummaryNameForm from './SummaryNameForm';
import SvgIcon from '@home/components/SvgIcon';
import PromptSection from '@op/pages/AIAnalysisHub/CustomAISummary/component/AISummarySetting/PromptSection';
import useCustomDefaultPrompt from '@op/pages/AIAnalysisHub/CustomAISummary/hooks/useCustomDefaultPrompt';
import usePromptCollect from '@op/pages/AIAnalysisHub/CustomAISummary/hooks/usePromptCollect';

export type PromptFormPropsType = {
  gameInfo: any;
  wholeGameInfo: any;
  filter: any;
  defaultActive?: number;
  promptList: PromptListType;
  hasAdvancedSummary?: boolean;
  needBasicTaskId?: boolean;
  scenario?: ScenarioType;
  businessType?: 'keyword_analysis';
  promptValue?: PromptConfirmItemType;
  hasResultOutput?: boolean;
  handleConfirm: PromptConfirmType;
  previewPrompt?: boolean;
  oriFilter?: any;
  saveName?: string;
  hasAgent?: boolean;
  modelType?: ModelType;
  isModal?: boolean;
};

export default function PromptForm({
  handleConfirm,
  handleCancel,
  filter,
  promptList = [],
  wholeGameInfo,
  historyVisible,
  toggleHistoricalPrompt,
  gameInfo,
  hasAdvancedSummary = true,
  needBasicTaskId = true,
  hasResultOutput = true,
  scenario,
  businessType,
  promptValue,
  saveName,
  userInfo,
  modelType,
  isModal = false,
}: {
  toggleHistoricalPrompt: () => void;
  historyVisible: boolean;
  handleCancel: () => void;
  userInfo: any;
} & PromptFormPropsType) {
  const { formatMessage } = useIntl();
  const history = useHistory();
  const [activePromptId, setActivePromptId] = useState('');
  const promptEdit = useRef(false);

  function getDefaultSaveName() {
    if (!saveName) return '';

    return `${saveName}-${DBO_TM.timeTitle(filter)} ${getReportSuffix()}`;
  }

  const {
    defaultPromptList,
  } = useCustomDefaultPrompt({ gameInfo: wholeGameInfo });

  const {
    resultType,
    emailList,
    aiSaveTitle,
    setAiSaveTitle,
    isSave,
    saveEmailList,
    remainder,
    total,
    setRemainder,
    advancedLimitLoading,
    gptOptions,
    setGptOptions,
    setErrorText,
    prompt,
    setPrompt,
    confirmDisabled,
    gptType,
    realPromptList,
    errorText,
  } = useSaveDetailForm({
    userInfo,
    getDefaultSaveName,
    defaultIsSave: !!saveName,
    promptList,
    promptValue,
    hasAdvancedSummary,
  });

  const {
    advancedConfirm,
    loading: confirmLoading,
    downloadAdvanceConfirm,
  } = useConfirm({
    filter,
    gameInfo,
    setRemainder,
    modelType,
  });

  const handleGptConfirm = async () => {
    const realResultType = hasResultOutput ? resultType : 'page';

    buriedBtnPoint('GPT_Confirm', gptType, gptOptions, realResultType);

    saveEmailList();

    const promptInfo: PromptConfirmItemType = {
      prompt,
      resultType: realResultType,
      gptOptions,
      scenario,
      businessType,
      modelType: gptType,
    };

    if (isSave) {
      promptInfo.saveName = aiSaveTitle;

      if (emailList.length > 0) {
        promptInfo.emailList = emailList;
      }
    }

    try {
      // 基础模式走后生成taskId模式
      if (gptOptions === 'basic' && !needBasicTaskId) {
        if (prompt?.trim() === '') return;
        setUserHistoryPrompt(prompt);
        let defaultAction = null;
        if (realResultType === 'download') {
          defaultAction = () => advancedConfirm(promptInfo);
        }
        return handleConfirm?.(promptInfo, defaultAction);
      }

      // 高级版本走先生成taskId模式
      const createResult = await advancedConfirm({
        ...promptInfo,
        resultType: 'page',
      });
      const { task_id: taskId } = createResult || {};
      if (realResultType === 'download') {
        if (!taskId) return;
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        return handleConfirm?.({ ...promptInfo, taskId }, () =>
          downloadAdvanceConfirm(taskId, promptInfo),
        );
      }
      if (taskId) {
        handleConfirm?.({ ...promptInfo, taskId }, null);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const {
    loading: historyLoading,
    userPromptList,
    savedPromptList,
    setUserHistoryPrompt,
    getUserHistoryPrompt,
    savePrompt,
    updatePrompt,
    deletePrompt,
    applyPrompt,
  } = usePromptCollect({
    gameInfo: wholeGameInfo,
    promptList: realPromptList,
  });

  if (historyVisible) {
    return (
      <HistoricalPrompt
        setPrompt={setPrompt}
        userPromptList={userPromptList}
        loading={historyLoading}
        closeHistoricalPrompt={toggleHistoricalPrompt}
        getUserHistoryPrompt={getUserHistoryPrompt}
      ></HistoricalPrompt>
    );
  }

  return (
    <>
      <Modal.Body>
        {/* Prompt Section */}
        <PromptSection
          prompt={prompt}
          setPrompt={setPrompt}
          gptOptions={gptOptions}
          errorText={errorText}
          setErrorText={setErrorText}
          realPromptList={realPromptList}
          defaultPromptList={defaultPromptList}
          userPromptList={savedPromptList}
          togglePromptModal={toggleHistoricalPrompt}
          onSavePrompt={savePrompt}
          onUpdatePrompt={updatePrompt}
          onDeletePrompt={deletePrompt}
          onApplyPrompt={applyPrompt}
          isModal={isModal}
          activePromptId={activePromptId}
          setActivePromptId={setActivePromptId}
          promptEdit={promptEdit}
        />
        <Form className="dbo-aigc-radio">
          <SummaryModeForm
            gptOptions={gptOptions}
            setGptOptions={(value: any) => {
              const defaultPrompt = getDefaultPromptText(realPromptList);
              const defaultPromptItem = getDefaultPromptMap(realPromptList);
              if (value === 'basic' && gptOptions === 'advanced') {
                if (promptEdit.current === false) {
                  setPrompt(defaultPrompt);
                  setErrorText('');
                  setActivePromptId(defaultPromptItem?.name?.['en-US'] || '');
                } else {
                  if (!prompt.trim()) {
                    setErrorText(formatMessage({ id: 'Please enter prompt.' }),);
                  }
                }
              } else if (value === 'advanced' && gptOptions === 'basic') {
                if (promptEdit.current === false) {
                  setPrompt('');
                }
                setActivePromptId('');
                setErrorText('');
              }
              setGptOptions(value);
            }}
            remainder={remainder}
            total={total}
            advancedLimitLoading={advancedLimitLoading}
          />
          <SummaryNameForm
            value={aiSaveTitle}
            onChange={setAiSaveTitle}
          />
          <Form.Item>
            <div className="dbo-aigc-summary-setting-footer">
              <span className="dbo-aigc-summary-setting-footer-autosave">
                {formatMessage({ id: 'Auto-saves to Summary List' })}
              </span>
              <div
                className="dbo-aigc-summary-setting-footer-viewlist"
                onClick={() => history.push(`/opinion/AIAnalysisHub/CustomAISummary?tab=summary&listTab=oneTime`)}
              >
                {formatMessage({ id: 'View list' })}
                <SvgIcon type="agent-right-arrow" className="dbo-aigc-summary-setting-footer-viewlist-arrow" />
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={handleGptConfirm}
          loading={confirmLoading}
          disabled={confirmDisabled}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </>
  );
}
